import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useBlogPosts, useBlogTags } from '@/hooks/useApi';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { Tag, Clock } from 'lucide-react';

export default function BlogPage() {
  const [params, setParams] = useSearchParams();
  const selectedTag = params.get('tag') || '';
  const setSelectedTag = (tag: string) => setParams(tag ? { tag } : {});
  const { data: posts, isLoading } = useBlogPosts({
    tag: selectedTag || undefined,
  });
  const { data: tags } = useBlogTags();

  useDocumentMeta('Blog', 'Thoughts, tutorials, and insights on software engineering');

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          Blog
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Thoughts, tutorials, and insights on software engineering
        </p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Posts List */}
        <div className="flex-1">
          {isLoading ? (
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-slate-800/50 rounded-xl p-6 animate-pulse border border-slate-200 dark:border-transparent"
                >
                  <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="space-y-6">
              {posts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/70 rounded-xl p-6 transition-all duration-200 border border-slate-200 dark:border-transparent hover:border-slate-300 dark:hover:border-slate-700/50 shadow-sm dark:shadow-none"
                >
                  <Link to={`/blog/${post.slug}`}>
                    {/* Title */}
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 group-hover:text-primary transition-colors mb-2">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-slate-600 dark:text-slate-400 line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
                      </span>
                    </div>

                    {/* Tags */}
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50 rounded"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800/50 rounded-xl p-8 text-center border border-slate-200 dark:border-transparent">
              <p className="text-slate-500 dark:text-slate-400">
                {selectedTag
                  ? `No posts found with tag "${selectedTag}"`
                  : 'No blog posts yet. Check back soon!'}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="w-full lg:w-64 flex-shrink-0">
          <div className="sticky top-24 space-y-6">
            {/* Tags */}
            <div className="bg-white dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-transparent">
              <h3 className="font-medium text-slate-900 dark:text-slate-200 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedTag('')}
                  className={`px-2 py-1 text-sm rounded transition-colors ${
                    !selectedTag
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  All
                </button>
                {tags?.map((tagItem) => (
                  <button
                    key={tagItem.tag}
                    onClick={() => setSelectedTag(tagItem.tag)}
                    className={`px-2 py-1 text-sm rounded transition-colors ${
                      selectedTag === tagItem.tag
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {tagItem.tag} ({tagItem.count})
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
